import { ApolloClient, DefaultOptions } from "apollo-client";
import { createPersistedQueryLink } from "apollo-link-persisted-queries";
import { setContext } from "apollo-link-context";
import { HttpLink } from "apollo-link-http";
import { ApolloLink } from "apollo-link";
import { InMemoryCache } from "apollo-cache-inmemory";
import {
  RulesCommit,
  RunningEnvironment,
  useGraphQlClientSettings,
} from "fe-web-components/settings/GraphqlSettingsPage";
import { isProduction } from "fe-common/utils/envGetter";
import {
  solverDevEndPoint,
  solverJDevLatestEndPoint,
  solverProdEndPoint,
} from "./endpoints";
import { isEmpty } from "lodash";
import { RetryLink } from "apollo-link-retry";
import { getIdToken } from "user-management/src/cognito/kahunUserAuth";
import inflate from "./inflate";

const ENDPOINT_LOCALHOST =
  typeof window !== "undefined" &&
  document?.location?.href.includes("localSolver");

let GQL_ENDPOINT_CACHED = isProduction()
  ? solverProdEndPoint
  : solverDevEndPoint;
let GQL_ENDPOINT = isProduction() ? solverProdEndPoint : solverDevEndPoint;

if (ENDPOINT_LOCALHOST) {
  GQL_ENDPOINT = "http://localhost:4000/";
  GQL_ENDPOINT_CACHED = "http://localhost:4000/";
}

function overrideEndPoint(url: string) {
  const settings = useGraphQlClientSettings();
  if (settings.runningEnv) {
    switch (settings.runningEnv) {
      case RunningEnvironment.DEVELOP:
        url = solverDevEndPoint;
        break;
      case RunningEnvironment.PRODUCTION:
        url = solverProdEndPoint;
        break;
      case RunningEnvironment.JDEVLATEST:
        url = solverJDevLatestEndPoint;
        break;
      case RunningEnvironment.CUSTOM:
        url = settings.endpoint;
        break;
    }
    // console.log("GRAPHQL ENDPOINT OVERRIDE", url);
  }
  return url;
}

function setupClient(url: string, paramHeaders: any) {
  // override endpoint if set
  url = overrideEndPoint(url);

  const httpLink = new HttpLink({
    //credentials: 'include',
    useGETForQueries: false,
    uri: url,
  });
  const apqLink = createPersistedQueryLink({
    useGETForHashedQueries: true,
  });

  const headers = setContext(async (_, context) => {
    const { headers } = context;
    const settings = useGraphQlClientSettings();

    if (settings && !isEmpty(settings)) {
      //console.table(settings);
    }

    const commitsFilter = settings.rules || RulesCommit.moderated;

    let accessToken = "";
    try {
      //TODO: I tis better to use access token, for since we use google login, we need the user's email from the id token.
      //This is relevant for clinic manage only, so it would be best to split the dao to different services.
      accessToken = await getIdToken();
    } catch {}

    return {
      headers: {
        ...headers,
        ...paramHeaders,
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
        "kahun-commits-filter": commitsFilter,
        "X-GraphQL-Deduplicate": "true",
        "kahun-source": "editor",
      },
    };
  });

  const postLink = setContext((operation, context) => {
    //  console.log(
    //   "webClient - postLink:",
    //   context.headers["Cache-Control"],
    //   operation.operationName,
    // );
    if (
      context.headers["Cache-Control"] === "no-cache" ||
      /DD|restoreCase|Patient|Plan|Graph|RunLoadersStatusQuery|metricsQF|[cC]onversation|nextResponse|metricsPaths|[uU]ser|clinicData|recentCodes|debugFor|patientCaseMetaData|backendModel|visits|groundText|getRecommendations|checkForPII/i.test(
        operation.operationName || "",
      )
    )
      return { fetchOptions: { method: "POST" } };
  });

  const defaultOptions: DefaultOptions = {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  };

  const inflateLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      return inflate(response);
    });
  });

  const DEFAULT_RETRY_COUNT = 5;
  const OPERATIONS_RETRY = {
    freeTextToPatientCase: 2,
  };

  const retryLink = new RetryLink({
    delay: {
      initial: 1000,
      max: Infinity,
      jitter: true,
    },
    attempts: (count, operation, error) => {
      console.error("retry", error, operation);
      //don't retry on 400
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }

      if (
        count >=
        (OPERATIONS_RETRY[operation.operationName] || DEFAULT_RETRY_COUNT)
      )
        return false;
      return !!error;
    },
  });

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      headers,
      retryLink,
      inflateLink,
      apqLink,
      postLink,
      httpLink,
    ]),
    defaultOptions: defaultOptions,
  });
}

let GQL_CLIENT: ApolloClient<any>;
let GQL_CLIENT_CACHED: ApolloClient<any>;

type gqlClientOptions = {
  log?: boolean;
  secretKey?: any;
};

const defaultGqlClientOptions: gqlClientOptions = {
  log: false,
  secretKey: null,
};

export async function gqlClient(
  headers,
  cached = true,
  options = defaultGqlClientOptions,
  forceClientReset = false,
) {
  const { log, secretKey } = options;
  let client = () => (cached ? GQL_CLIENT_CACHED : GQL_CLIENT);
  if (!client() || forceClientReset) {
    await makeApolloClients(log, {
      ...headers,
      "Internal-Authorization": secretKey,
    });
  }
  return client() || GQL_CLIENT_CACHED;
}

async function makeApolloClients(log = false, header = null) {
  GQL_CLIENT_CACHED = setupClient(GQL_ENDPOINT_CACHED, header);
  try {
    log && console.log("AUTH HEADER ....");
    log && console.log("AUTH HEADER DOWNLOADED", header != null);
    if (header) GQL_CLIENT = setupClient(GQL_ENDPOINT, header);
  } catch (e) {
    console.error("gql web client: cant get authentication header", e);
  }
}

function gqlEndpoint() {
  let envEndpoint = process.env["KAHUN_GQL_ENDPOINT"];
  let result = envEndpoint || GQL_ENDPOINT;
  console.log("GQL ENDPOINT ", result);
  return result;
}

// primary function to retrieve cookie by name
function getCookie(name) {
  var arg = name + "=";
  var alen = arg.length;
  var clen = document.cookie.length;
  var i = 0;
  while (i < clen) {
    var j = i + alen;
    if (document.cookie.substring(i, j) == arg) {
      return getCookieVal(j);
    }
    i = document.cookie.indexOf(" ", i) + 1;
    if (i == 0) break;
  }
  return "";
}

// utility function called by getCookie( )
function getCookieVal(offset) {
  var endstr = document.cookie.indexOf(";", offset);
  if (endstr == -1) {
    endstr = document.cookie.length;
  }
  return unescape(document.cookie.substring(offset, endstr));
}
